/* eslint-disable filename-rules/match */

import { useMemo } from "react";

import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { sectionUserSelectors, selectCurrentUserId } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { useLocation } from "utils/routing";
import { HomeRoleType, homeUrlForRole } from "utils/urls";

export const getDefaultHomeRole = (canAuthorOrg: boolean, isStaffInAnySection: boolean) => {
  return canAuthorOrg ? "admin" : isStaffInAnySection ? "instructor" : "student";
};

export const useHomeRole = () => {
  const location = useLocation();
  const userId = useAppSelector(selectCurrentUserId);

  // We need to load section and org users to determine roles
  // Skip API calls if userId is undefined (user not logged in)
  const { isLoading } = usePromiseEffect(() => {
    if (!userId) return Promise.resolve(undefined);

    return Promise.all([
      storeApi.section_users.list({ user_id: userId }),
      storeApi.org_users.list({ user_id: userId }),
    ]);
  }, [userId]);

  const { canAuthorOrg } = useCapabilities();
  const sectionUsers = useAppSelector(sectionUserSelectors.filterForCurrentUser);
  const isStaffInAnySection = sectionUsers.some((cu) => cu.role !== "student");
  const isSectionAdminInAnySection = sectionUsers.some((cu) => cu.role === "section_admin");
  const isStudentInAnySection = sectionUsers.some((cu) => cu.role === "student");

  const availableRoles: HomeRoleType[] = useMemo(
    () =>
      [
        canAuthorOrg && "admin",
        isStaffInAnySection && "instructor",
        isSectionAdminInAnySection && "section_admin",
        isStudentInAnySection && "student",
      ].filter(Boolean) as HomeRoleType[],
    [canAuthorOrg, isSectionAdminInAnySection, isStaffInAnySection, isStudentInAnySection],
  );

  const currentRole =
    availableRoles.find((role) => location.pathname.startsWith(homeUrlForRole(role))) || "student";

  return { currentRole, availableRoles, isLoading };
};
